import { useCallback, useState, type CSSProperties, type ReactNode } from 'react'
import { ArrowDown, ArrowUp, Lock, RotateCcw } from 'lucide-react'
import { iconOnly, primaryButton, quietButton, secondaryButton, small } from '@/components/shell/StatusScreens'
import { Drawer } from '@/components/ui/Drawer'
import { cn } from '@/lib/utils'
import { ROLE_LABELS } from '../../../admin/shared/permissions'
import { Chip } from '../../../admin/shared/ui'
import { FRENTE_KEYS, type ErpSettings, type FrenteKey } from '../../contract'
import { ERP_VIEWS, EXPERIENCE_KEYS, EXPERIENCES, FRENTES, frenteNav, frentePii, type ExperienceKey } from '../../frentes'
import { HowItWorks } from '../../ui/calm'
import { EXPERIENCE_ICONS } from '../../ui/experience-icons'
import {
    cellLabel, defaultNavKeys, entryExperienceOf, experienceCell, isCustomNav, moveWithinExperience, navKeysOf, rolesOpening, setExperienceViews, toggleExperienceView,
} from '../settings-logic'
import { Note, SettingRow, SettingsScreen, Switch } from '../ui'

/* Configurações › ERP › Frentes e telas: a matriz Frente × Experiência (protótipo settings-people.js). Cada célula diz
   quanto da experiência a frente vê (Completa · "n de m" · —) e marca a experiência de entrada. Clicar numa célula abre o
   painel das telas daquela experiência (liga/desliga, ordem das abas, Liberar tudo · Só a visão · Tirar); clicar na
   frente abre entrada, dados pessoais, quem abre e "Restaurar padrão". A Tecnologia fica travada (vê tudo).
   Salva em settings.frentes[k].nav / .pii; o menu da frente muda na hora (a casca usa frenteNav(frente, perfil, settings)). */

/** Cor de cada experiência (os tons dos tipos de cadastro do brand). */
const XP_COLORS: Record<ExperienceKey, string> = {
    sponsor: 'var(--t-doador)', staff: 'var(--t-consultor)', corporate: 'var(--t-empresa)', faith: 'var(--t-igreja)', ambassadors: 'var(--t-embaixador)', osp: 'var(--t-osp)',
}

/** A Tecnologia administra o sistema e vê tudo: a linha dela na matriz não se edita. */
const LOCKED: FrenteKey = 'tecnologia'

type Open = { kind: 'cell'; frente: FrenteKey; xp: ExperienceKey } | { kind: 'frente'; frente: FrenteKey } | null

export function FrentesSection() {
    const [open, setOpen] = useState<Open>(null)
    const close = useCallback(() => setOpen(null), [])
    return (
        <SettingsScreen
            title="Frentes e telas"
            head={s => {
                const custom = FRENTE_KEYS.filter(k => isCustomNav(k, s))
                return {
                    title: `${FRENTE_KEYS.length} frentes × ${EXPERIENCE_KEYS.length} experiências: cada frente vê só o que precisa.`,
                    sub: custom.length ? `Fora do padrão: ${custom.map(k => FRENTES[k].label).join(', ')}.` : 'Todas as frentes estão no menu padrão.',
                }
            }}
        >
            {(s, save, saving) => {
                const setNav = (frente: FrenteKey, keys: string[]) => save({ frentes: { [frente]: { nav: keys } } })
                return (
                    <>
                        <Matrix settings={s} onCell={(frente, xp) => setOpen({ kind: 'cell', frente, xp })} onFrente={frente => setOpen({ kind: 'frente', frente })} />
                        <Legend />
                        <HowItWorks>
                            <p className="m-0">Cada célula mostra quantas telas da experiência aparecem no menu da frente. Clique para escolher as telas; clique no nome da frente para dados pessoais e para voltar ao padrão.</p>
                            <p className="m-0">Ligar qualquer tela liga também a Visão da experiência (Hoje · A IA fez · Carteira), que é a porta dela. Desligar a Visão tira a experiência inteira do menu.</p>
                            <p className="m-0">A entrada é a experiência que abre quando a pessoa entra no Staff. Ela vem do padrão da frente; se a frente perder essa experiência, entra pela primeira do menu.</p>
                            <p className="m-0">Vale na hora para todas as pessoas da frente e fica na Auditoria. Cada pessoa só vê as telas que o perfil dela abre. A Tecnologia vê tudo.</p>
                        </HowItWorks>
                        {open?.kind === 'cell' && (
                            <CellDrawer frente={open.frente} xp={open.xp} settings={s} saving={saving} onNav={keys => setNav(open.frente, keys)} onClose={close} />
                        )}
                        {open?.kind === 'frente' && (
                            <FrenteDrawer frente={open.frente} settings={s} saving={saving} onNav={keys => setNav(open.frente, keys)} onPii={pii => save({ frentes: { [open.frente]: { pii } } })} onClose={close} />
                        )}
                    </>
                )
            }}
        </SettingsScreen>
    )
}

function Matrix({ settings, onCell, onFrente }: { settings: ErpSettings; onCell: (f: FrenteKey, x: ExperienceKey) => void; onFrente: (f: FrenteKey) => void }) {
    return (
        <div className="overflow-x-auto rounded-[14px] border border-line bg-panel">
            <table className="w-full min-w-[820px] border-collapse text-[13px]">
                <caption className="sr-only">Frente × Experiência: o que cada frente vê no menu</caption>
                <thead>
                    <tr>
                        <th scope="col" className="border-b border-line px-3 py-2.5 text-left text-[12px] font-semibold text-muted">Frente</th>
                        {EXPERIENCE_KEYS.map(x => {
                            const Icon = EXPERIENCE_ICONS[x]
                            return (
                                <th key={x} scope="col" className="border-b border-line px-1.5 py-2.5 text-left text-[12px] font-semibold whitespace-nowrap text-muted">
                                    <span className="inline-flex items-center gap-1.5"><Icon size={13} aria-hidden="true" style={{ color: XP_COLORS[x] }} />{EXPERIENCES[x].label}</span>
                                </th>
                            )
                        })}
                    </tr>
                </thead>
                <tbody>
                    {FRENTE_KEYS.map(k => {
                        const keys = navKeysOf(k, settings)
                        const entry = entryExperienceOf(k, settings)
                        const locked = k === LOCKED
                        const def = FRENTES[k]
                        return (
                            <tr key={k} className="border-t border-line first:border-t-0">
                                <th scope="row" className="px-1.5 py-1 text-left font-normal">
                                    <button type="button" onClick={() => onFrente(k)} className="flex w-full min-w-[180px] cursor-pointer flex-col gap-px rounded-[8px] border-0 bg-transparent px-1.5 py-1 text-left hover:bg-[var(--hover)]">
                                        <span className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-fg">
                                            {def.label}
                                            {locked && <Lock size={12} aria-label="travada" className="text-muted" />}
                                            {isCustomNav(k, settings) && <span title="Menu fora do padrão" className="size-1.5 rounded-full bg-[var(--info)]"><span className="sr-only">menu fora do padrão</span></span>}
                                        </span>
                                        <span className="text-[11.5px] text-muted">{def.role}{frentePii(k, settings) ? ' · vê dados pessoais' : ''}</span>
                                    </button>
                                </th>
                                {EXPERIENCE_KEYS.map(x => {
                                    const c = experienceCell(keys, x)
                                    const label = cellLabel(c)
                                    const color = XP_COLORS[x]
                                    return (
                                        <td key={x} className="px-1 py-1">
                                            <button
                                                type="button"
                                                disabled={locked}
                                                title={locked ? 'A Tecnologia vê tudo' : undefined}
                                                onClick={() => onCell(k, x)}
                                                aria-label={`${def.label} · ${EXPERIENCES[x].label}: ${label === '—' ? 'não vê' : label}${entry === x ? ', experiência de entrada' : ''}`}
                                                className={cn(
                                                    'flex h-[34px] w-full min-w-[96px] cursor-pointer items-center justify-center gap-1.5 rounded-[8px] border-0 px-1.5 text-[12px] whitespace-nowrap hover:brightness-[.97] disabled:cursor-default',
                                                    c.state === 'none' ? 'font-medium text-muted' : 'font-semibold text-fg',
                                                )}
                                                style={c.state === 'full'
                                                    ? { background: `color-mix(in srgb, ${color} 18%, var(--panel))` }
                                                    : c.state === 'part'
                                                        ? { background: `color-mix(in srgb, ${color} 7%, var(--panel))`, boxShadow: `inset 0 0 0 1px color-mix(in srgb, ${color} 35%, transparent)` }
                                                        : { background: 'var(--panel)', boxShadow: 'inset 0 0 0 1px var(--line)' }}
                                            >
                                                {label}
                                                {entry === x && <EntryBadge />}
                                            </button>
                                        </td>
                                    )
                                })}
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}

function EntryBadge() {
    return <span title="Abre por aqui quando a pessoa entra" className="rounded-full bg-fg px-1.5 text-[10px] leading-[16px] font-semibold text-panel">entrada</span>
}

function Legend() {
    const sw = (style: CSSProperties) => <i aria-hidden="true" className="inline-block size-3 rounded-[3px] align-[-2px]" style={style} />
    return (
        <p className="m-0 flex flex-wrap gap-x-4 gap-y-1 px-0.5 text-[12px] text-muted">
            <span className="inline-flex items-center gap-1.5">{sw({ background: 'color-mix(in srgb, var(--t-doador) 18%, var(--panel))' })}Completa: todas as telas</span>
            <span className="inline-flex items-center gap-1.5">{sw({ boxShadow: 'inset 0 0 0 1px color-mix(in srgb, var(--t-doador) 35%, transparent)' })}Parcial: só algumas</span>
            <span className="inline-flex items-center gap-1.5">{sw({ boxShadow: 'inset 0 0 0 1px var(--line)' })}— não vê</span>
            <span className="inline-flex items-center gap-1.5"><EntryBadge />abre ao entrar</span>
        </p>
    )
}

/** Painel de uma célula: as telas da experiência no menu da frente. */
function CellDrawer({ frente, xp, settings, saving, onNav, onClose }: {
    frente: FrenteKey; xp: ExperienceKey; settings: ErpSettings; saving: boolean; onNav: (keys: string[]) => void; onClose: () => void
}) {
    const e = EXPERIENCES[xp]
    const def = FRENTES[frente]
    const keys = navKeysOf(frente, settings)
    const c = experienceCell(keys, xp)
    const entry = entryExperienceOf(frente, settings) === xp
    const onKeys = keys.filter(k => e.views.includes(k))
    const off = e.views.filter(k => !keys.includes(k))
    const apply = (next: string[]) => { if (next.join() !== keys.join()) onNav(next) }
    const wouldEmpty = keys.every(k => e.views.includes(k))
    return (
        <Drawer
            open
            onClose={onClose}
            width={460}
            title={`${def.label} · ${e.label}`}
            subtitle={`${c.on} de ${c.total} telas · ${!c.on ? 'esta frente não vê esta experiência' : entry ? 'é a entrada desta frente' : 'aparece no menu'}`}
            footer={<button type="button" className={primaryButton} onClick={onClose}>Pronto</button>}
        >
            <div className="flex flex-col gap-3">
                <div className="flex flex-wrap gap-1.5">
                    <button type="button" className={cn(secondaryButton, small)} disabled={saving || c.state === 'full'} onClick={() => apply(setExperienceViews(keys, xp, 'all'))}>Liberar tudo</button>
                    <button type="button" className={cn(secondaryButton, small)} disabled={saving || (c.on === 1 && keys.includes(e.overview))} onClick={() => apply(setExperienceViews(keys, xp, 'vision'))}>Só a visão</button>
                    <button type="button" className={cn(quietButton, small)} disabled={saving || !c.on || wouldEmpty} title={wouldEmpty ? 'A frente precisa de pelo menos uma tela no menu' : undefined} onClick={() => apply(setExperienceViews(keys, xp, 'none'))}>Tirar</button>
                </div>
                {onKeys.length > 0 && (
                    <ol aria-label={`Telas de ${e.label} no menu de ${def.label}, na ordem das abas`} className="m-0 flex list-none flex-col p-0">
                        {onKeys.map((key, i) => (
                            <ViewRow key={key} viewKey={key} on overview={key === e.overview} disabled={saving || (keys.length <= 1)} onToggle={() => apply(toggleExperienceView(keys, key, false))}>
                                <button type="button" className={cn(quietButton, small, iconOnly)} aria-label={`Subir ${ERP_VIEWS[key]?.label}`} disabled={saving || i === 0} onClick={() => apply(moveWithinExperience(keys, key, -1))}><ArrowUp aria-hidden="true" /></button>
                                <button type="button" className={cn(quietButton, small, iconOnly)} aria-label={`Descer ${ERP_VIEWS[key]?.label}`} disabled={saving || i === onKeys.length - 1} onClick={() => apply(moveWithinExperience(keys, key, 1))}><ArrowDown aria-hidden="true" /></button>
                            </ViewRow>
                        ))}
                    </ol>
                )}
                {off.length > 0 && (
                    <div className="flex flex-col gap-1">
                        <span className="text-[12px] font-semibold text-muted">Fora do menu ({off.length})</span>
                        <ul className="m-0 flex list-none flex-col p-0">
                            {off.map(key => <ViewRow key={key} viewKey={key} on={false} overview={key === e.overview} disabled={saving} onToggle={() => apply(toggleExperienceView(keys, key, true))} />)}
                        </ul>
                    </div>
                )}
                <Note>Ligar qualquer tela liga também a Visão, que é a porta da experiência. Cada pessoa só vê as telas que o perfil dela abre.</Note>
                <MenuPreview frente={frente} settings={settings} />
            </div>
        </Drawer>
    )
}

function ViewRow({ viewKey, on, overview, disabled, onToggle, children }: { viewKey: string; on: boolean; overview: boolean; disabled: boolean; onToggle: () => void; children?: ReactNode }) {
    const v = ERP_VIEWS[viewKey]
    if (!v) return null
    return (
        <li className="flex min-h-11 items-center gap-2.5 border-t border-line py-1 first:border-t-0">
            <Switch checked={on} label={`${v.label} (${v.path}) aparece no menu`} disabled={disabled} onChange={onToggle} />
            <span className="flex min-w-0 flex-1 flex-col leading-[1.3]">
                <span className="truncate text-[13.5px]">{v.label}{overview && <span className="text-[12px] text-muted"> · Hoje, A IA fez, Carteira</span>}</span>
                <span className="truncate text-[11.5px] text-muted">{v.path}</span>
            </span>
            {children && <span className="flex flex-none gap-0.5">{children}</span>}
        </li>
    )
}

/** Painel da frente: entrada, dados pessoais, quem abre e voltar ao padrão. */
function FrenteDrawer({ frente, settings, saving, onNav, onPii, onClose }: {
    frente: FrenteKey; settings: ErpSettings; saving: boolean; onNav: (keys: string[]) => void; onPii: (on: boolean) => void; onClose: () => void
}) {
    const def = FRENTES[frente]
    const entry = entryExperienceOf(frente, settings)
    const defaultEntry = EXPERIENCES[EXPERIENCE_KEYS.find(x => EXPERIENCES[x].overview === def.entry) ?? 'staff']
    const pii = frentePii(frente, settings)
    const custom = isCustomNav(frente, settings)
    const roles = rolesOpening(frente)
    return (
        <Drawer
            open
            onClose={onClose}
            width={460}
            title={def.label}
            subtitle={def.role}
            footer={<button type="button" className={primaryButton} onClick={onClose}>Pronto</button>}
        >
            <div className="flex flex-col gap-3">
                <div>
                    <SettingRow title="Experiência de entrada" hint={entry && entry !== defaultEntry.key ? `${defaultEntry.label} (o padrão) saiu do menu; entra pela primeira experiência do menu.` : 'Abre quando a pessoa entra no Staff. Vem do padrão da frente.'}>
                        <span className="text-[13.5px] font-semibold">{entry ? EXPERIENCES[entry].label : 'Início'}</span>
                    </SettingRow>
                    <SettingRow title="Vê dados pessoais" hint="CPF, e-mail e celular, sempre mascarados até “Mostrar”.">
                        {pii !== def.pii && <Chip tone="info">fora do padrão</Chip>}
                        {frente === LOCKED && pii && <Chip tone="warn">quebra a separação de funções</Chip>}
                        <Switch checked={pii} disabled={saving} label={`${def.label}: vê dados pessoais`} onChange={onPii} />
                    </SettingRow>
                    <SettingRow title="Quem abre esta frente" hint="Vem do perfil de cada pessoa.">
                        <span className="text-right text-[13px] text-muted">{roles.map(r => ROLE_LABELS[r]).join(', ') || 'ninguém'}</span>
                    </SettingRow>
                    <SettingRow title="Menu" hint={custom ? 'Diferente do padrão da frente.' : 'No padrão da frente.'}>
                        <button type="button" className={cn(secondaryButton, small)} disabled={!custom || saving} onClick={() => onNav(defaultNavKeys(frente))}>
                            <RotateCcw aria-hidden="true" />Restaurar padrão
                        </button>
                    </SettingRow>
                </div>
                {frente === LOCKED && <Note>A Tecnologia administra o sistema e vê todas as experiências; a linha dela na matriz não se edita.</Note>}
                <MenuPreview frente={frente} settings={settings} />
            </div>
        </Drawer>
    )
}

/** Prévia do menu da frente, como o administrador vê (experiência + abas). */
function MenuPreview({ frente, settings }: { frente: FrenteKey; settings: ErpSettings }) {
    const entries = frenteNav(frente, 'admin', settings)
    const entry = entryExperienceOf(frente, settings)
    const groups: { area: string; labels: string[] }[] = []
    for (const e of entries) {
        let g = groups.find(x => x.area === e.area)
        if (!g) groups.push(g = { area: e.area, labels: [] })
        g.labels.push(e.view.label)
    }
    return (
        <aside aria-label={`Prévia do menu de ${FRENTES[frente].label}`} className="flex flex-col gap-1.5 rounded-[12px] border border-line px-3 py-2.5 text-[13px]">
            <span className="text-[11.5px] font-semibold text-muted">Prévia do menu</span>
            {!groups.length && <span className="text-muted">Nenhuma tela.</span>}
            {groups.map(g => {
                const x = EXPERIENCE_KEYS.find(k => EXPERIENCES[k].label === g.area)
                return (
                    <div key={g.area || '_'} className="flex flex-col gap-0.5">
                        <span className="inline-flex items-center gap-2 font-semibold">
                            <i aria-hidden="true" className="size-2 rounded-[2px]" style={{ background: x ? XP_COLORS[x] : 'var(--line-2)' }} />
                            {g.area || 'Solto'}
                            {x && x === entry && <EntryBadge />}
                        </span>
                        <span className="pl-4 text-[12px] text-muted">{g.labels.join(' · ')}</span>
                    </div>
                )
            })}
        </aside>
    )
}
